import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import { ActionMenu, ActionMenuItem } from "../../components/ui/ActionMenu";
import { BulkBar } from "../../components/admin/dataTable/BulkBar";
import { useSearchParams } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Skeleton } from "../../components/ui/Skeleton";
import { StatusMessage, mapApiError } from "../../components/ui/StatusMessage";
import { useToast } from "../../components/ui/Toast";
import { MathText } from "../../components/MathText";
import { api } from "../../lib/api";
import { IngestChapterGroup } from "../../components/admin/IngestChapterGroup";
import type { ChapterGroupCounts } from "../../lib/ingestChapterApi";
import { IngestBlockedDialog, type BlockChoice } from "../../components/admin/IngestBlockedDialog";
import type { BridgeBlock, ChapterMode, FigureDecision, IngestItem, IngestItemDetail, IngestStatus } from "../../lib/ingestReviewApi";
import { useAdminToken } from "./adminShared";

/** ==term== is the pipeline's highlight markup. Shown as a mark here; the bridge turns it into the student key-term markup. */
function Md({ children, inline }: { children?: string | null; inline?: boolean }) {
  const parts = (children ?? "").split(/(==[^=]+==)/g);
  if (parts.length === 1) return <MathText inline={inline}>{children}</MathText>;
  return (
    <span>
      {parts.map((p, i) =>
        p.startsWith("==") && p.endsWith("==") ? <mark key={i} className="bg-accent-soft text-foreground">{p.slice(2, -2)}</mark> : <MathText key={i} inline>{p}</MathText>,
      )}
    </span>
  );
}

function Fig({ id, data, caption, alt }: { id?: string | null; data: Record<string, string>; caption?: string; alt?: string }) {
  const src = id ? data[id] : undefined;
  if (!src) return <p className="text-sm text-muted-foreground">Figure {id ?? ""} is not uploaded.</p>;
  return (
    <figure className="space-y-1">
      <img src={src} alt={alt || caption || "Figure"} className="max-h-80 rounded-lg border border-border bg-card" />
      {caption && <figcaption className="text-xs text-muted-foreground">{caption}</figcaption>}
    </figure>
  );
}

export function Block({ b, data, onRemoveFigure }: { b: Record<string, any>; data: Record<string, string>; onRemoveFigure?: (id: string) => void }) {
  const generated = b.origin === "generated";
  let body: ReactNode = null;
  switch (b.type) {
    case "heading": body = <h4 className="font-display text-lg font-semibold"><Md inline>{b.text}</Md></h4>; break;
    case "paragraph": body = <div className="text-base"><Md>{b.md}</Md></div>; break;
    case "list": {
      const Tag = b.style === "numbered" ? "ol" : "ul";
      body = <Tag className={`${b.style === "numbered" ? "list-decimal" : "list-disc"} space-y-1 pl-5 text-base`}>{(b.items ?? []).map((x: string, i: number) => <li key={i}><Md inline>{x}</Md></li>)}</Tag>;
      break;
    }
    case "definition_card": body = <p className="text-base"><strong>{b.term}</strong>: <Md inline>{b.definition_md}</Md></p>; break;
    case "formula":
      body = (
        <div className="space-y-1 text-base">
          <p className="font-semibold">{b.name}</p>
          <MathText>{`$$${b.latex}$$`}</MathText>
          {(b.variables ?? []).map((v: any, i: number) => <p key={i} className="text-sm text-muted-foreground"><MathText inline>{`$${v.symbol}$`}</MathText> {v.meaning}{v.unit ? ` (${v.unit})` : ""}</p>)}
          {b.when_to_use && <p className="text-sm">{b.when_to_use}</p>}
        </div>
      );
      break;
    case "worked_example":
      body = (
        <div className="space-y-1 text-base">
          <Md>{b.problem_md}</Md>
          <ol className="list-decimal space-y-1 pl-5">{(b.steps ?? []).map((s: string, i: number) => <li key={i}><Md inline>{s}</Md></li>)}</ol>
          <p className="font-semibold"><Md inline>{b.answer_md}</Md></p>
          {(b.figure_external_ids ?? []).map((f: string) => <Fig key={f} id={f} data={data} />)}
        </div>
      );
      break;
    case "highlight": body = <div className="rounded-lg border border-primary bg-accent-soft p-3 text-base"><Md>{b.md}</Md></div>; break;
    case "callout": body = <div className="rounded-lg border border-border bg-soft p-3 text-base"><Badge variant="info">{String(b.variant).replace("_", " ")}</Badge> <Md inline>{b.md}</Md></div>; break;
    case "table":
      body = (
        <div className="overflow-x-auto admin-scroll-x">
          {b.caption && <p className="mb-1 text-sm font-semibold">{b.caption}</p>}
          <table className="w-full border border-border text-sm">
            <thead><tr>{(b.columns ?? []).map((c: string, i: number) => <th key={i} className="border border-border bg-muted p-2 text-left"><Md inline>{c}</Md></th>)}</tr></thead>
            <tbody>{(b.rows ?? []).map((r: string[], i: number) => <tr key={i}>{r.map((c, j) => <td key={j} className="border border-border p-2"><Md inline>{c}</Md></td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      break;
    case "figure":
      body = (
        <div className="space-y-1">
          <Fig id={b.figure_external_id} data={data} caption={b.caption} alt={b.alt_text} />
          {onRemoveFigure && <Button size="sm" variant="ghost" onClick={() => onRemoveFigure(b.figure_external_id)}>Remove image</Button>}
        </div>
      );
      break;
    case "chart":
      body = (
        <div className="space-y-1 text-sm">
          <p className="font-semibold">{b.title} ({b.chart_type})</p>
          {(b.series ?? []).map((s: any, i: number) => (
            <p key={i} className="text-muted-foreground">{s.name}: {(s.points ?? []).map((p: any) => `${p.x}: ${p.y}`).join(", ")}</p>
          ))}
          <p className="text-xs text-muted-foreground">x: {b.x_label} / y: {b.y_label}</p>
        </div>
      );
      break;
    case "steps": body = <ol className="list-decimal space-y-1 pl-5 text-base">{(b.steps ?? []).map((s: any, i: number) => <li key={i}><strong>{s.label}</strong> <Md inline>{s.md}</Md></li>)}</ol>; break;
    case "key_point_card":
    case "summary_card":
      body = (
        <div className="rounded-lg border border-border bg-card-child p-3 text-base">
          <p className="font-semibold">{b.title}</p>
          {b.md && <Md>{b.md}</Md>}
          {b.points && <ul className="list-disc space-y-1 pl-5">{b.points.map((p: string, i: number) => <li key={i}><Md inline>{p}</Md></li>)}</ul>}
        </div>
      );
      break;
    case "self_check":
      body = <ol className="list-decimal space-y-2 pl-5 text-base">{(b.items ?? []).map((x: any, i: number) => <li key={i}><Md inline>{x.prompt_md}</Md><div className="text-sm text-muted-foreground">Answer: <Md inline>{x.answer_md}</Md></div></li>)}</ol>;
      break;
    default: body = <pre className="overflow-x-auto text-xs">{JSON.stringify(b, null, 2)}</pre>;
  }
  return (
    <div className="space-y-1">
      {generated && <Badge variant="warning">generated</Badge>}
      {body}
    </div>
  );
}

export function Question({ p, data, onRemoveFigure }: { p: Record<string, any>; data: Record<string, string>; onRemoveFigure?: (id: string) => void }) {
  const correct: string[] = p.correct_answer?.labels ?? [];
  return (
    <div className="space-y-2 text-base">
      <p className="text-xs text-muted-foreground">{p.exam?.label ?? p.chapter_ref?.title} {p.section ? `· ${p.section}` : ""} · Q{p.question_number} · {p.question_format}</p>
      <Md>{p.question_text}</Md>
      {(p.figures ?? []).filter((f: any) => f.role !== "option").map((f: any) => (
        <div key={f.figure_external_id} className="space-y-1">
          <Fig id={f.figure_external_id} data={data} caption={f.caption} />
          {onRemoveFigure && <Button size="sm" variant="ghost" onClick={() => onRemoveFigure(f.figure_external_id)}>Remove image</Button>}
        </div>
      ))}
      <ul className="space-y-1">
        {(p.options ?? []).map((o: any) => (
          <li key={o.label} className={`rounded-lg border p-2 ${correct.includes(o.label) ? "border-success bg-soft" : "border-border"}`}>
            <strong>{o.label}.</strong> <Md inline>{o.text}</Md>
            {o.figure_external_id && <Fig id={o.figure_external_id} data={data} />}
          </li>
        ))}
      </ul>
      <p className="text-sm text-muted-foreground">Answer: {correct.length ? correct.join(", ") : p.correct_answer?.text ?? "none"} ({p.correct_answer?.source ?? "none"})</p>
      {p.explanation && <div className="text-sm"><strong>Explanation:</strong> <Md>{p.explanation}</Md></div>}
      {p.origin === "generated" && (
        <div className="space-y-1 text-sm text-muted-foreground">
          <p>Difficulty: {p.difficulty} · Level: {p.bloom_level} · Checked by: {p.verification?.model ?? "-"} (grounded {String(p.verification?.grounded)}, solver agrees {String(p.verification?.solver_agrees)}, steps {p.verification?.steps_needed})</p>
          {(p.source_refs ?? []).map((r: any, i: number) => <p key={i}>Source: "{r.quote}"</p>)}
          {(p.distractor_notes ?? []).map((d: any, i: number) => <p key={i}>Wrong option {d.label}: {d.note}</p>)}
        </div>
      )}
    </div>
  );
}

/** What the approval recorded for an item that is not (fully) with students. */
function bridgeState(item: IngestItem): { label: string; variant: "warning" | "info"; reason: string } | null {
  const ref = item.published_ref as { bridge?: string; reason?: string } | null;
  if (!ref?.reason) return null;
  if (item.status === "draft" && ref.bridge === "blocked") return { label: "Needs a decision", variant: "warning", reason: ref.reason };
  if (item.status === "published" && ref.bridge === "waiting") return { label: "Approved, waiting for its topic", variant: "info", reason: ref.reason };
  if (item.status === "published" && ref.bridge === "skipped") return { label: "Approved, not shown to students", variant: "info", reason: ref.reason };
  return null;
}

function ItemCard({ item, selected, onSelect, token, onChanged, onBlocked, mode, askReplace }: { item: IngestItem; selected: boolean; onSelect: (v: boolean) => void; token: string; onChanged: () => void; onBlocked: (b: BridgeBlock[]) => void; mode: ChapterMode; askReplace: (run: () => void) => void }) {
  const { showToast } = useToast();
  const [detail, setDetail] = useState<IngestItemDetail | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    try { setDetail(await api.admin.ingestReview.get(token, item.id)); } catch (e) { showToast("error", mapApiError(e).message); }
  }, [token, item.id, showToast]);
  useEffect(() => { if (open && !detail) void load(); }, [open, detail, load]);

  const act = async (status: IngestStatus, n?: string, edits?: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = await api.admin.ingestReview.patch(token, item.id, { status, note: n, edits, ...(status === "published" ? { on_conflict: mode, confirm_replace: mode === "replace" } : {}) });
      r.warnings?.forEach((w) => showToast("info", w));
      if (r.blocked?.length) {
        showToast("info", "Not approved yet. Your decision is needed.");
        onBlocked(r.blocked);
      } else showToast("success", status === "published" ? "Approved" : status === "rejected" ? "Rejected" : "Saved");
      setRejecting(false); setEditing(false); setDetail(null);
      onChanged();
    } catch (e) { showToast("error", mapApiError(e).message); } finally { setBusy(false); }
  };

  const canEditImages = item.status !== "published";
  const changeFigure = async (figureId: string, action: "remove" | "restore") => {
    setBusy(true);
    try {
      setDetail(await api.admin.ingestReview.figure(token, item.id, figureId, action));
      showToast("success", action === "remove" ? "Image removed" : "Image restored");
      onChanged();
    } catch (e) { showToast("error", mapApiError(e).message); } finally { setBusy(false); }
  };
  const loadDiscarded = async () => {
    try { setDetail(await api.admin.ingestReview.get(token, item.id, true)); } catch (e) { showToast("error", mapApiError(e).message); }
  };

  const state = bridgeState(item);
  const p = (detail ?? item).payload;
  const decisions = (p.figure_decisions ?? []) as FigureDecision[];
  const discarded = decisions.filter((d) => d.decision === "discarded");
  const keptCount = (p.figures ?? []).length;
  const title = item.kind === "module_topic" ? `${p.chapter?.title ?? ""} · ${p.topic?.title ?? ""}` : `${p.exam?.university ? p.exam.university + " " : ""}${item.resource_type === "coc_exam" ? `COC sem ${item.semester} ` : ""}${p.exam?.label ?? p.chapter_ref?.title ?? ""} · Q${p.question_number ?? ""}`;
  const data = detail?.figure_data ?? {};
  const editable = item.kind === "exam_question"
    ? { question_text: p.question_text, options: p.options, correct_answer: p.correct_answer, explanation: p.explanation }
    : { blocks: p.blocks };

  return (
    <Card className={`space-y-3 p-4 transition-[border-color] duration-200 hover:border-primary/40 ${selected ? "border-primary/60 bg-accent-soft" : ""}`}>
      <div className="flex items-start gap-3">
        <input type="checkbox" aria-label="Select" checked={selected} onChange={(e) => onSelect(e.target.checked)} className="mt-3 h-5 w-5 shrink-0" />
        <button type="button" className="min-h-11 min-w-0 flex-1 text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="block text-base font-semibold text-foreground">{title}</span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {item.source?.file} p.{item.source?.page_start}{item.source?.page_end && item.source.page_end !== item.source.page_start ? `-${item.source.page_end}` : ""}
          </span>
        </button>
        <Badge variant={item.status === "published" ? "success" : item.status === "rejected" ? "destructive" : "warning"} className="mt-2.5 shrink-0">{item.status === "published" ? "approved" : item.status}</Badge>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pl-8">
        <Badge variant="secondary">{item.resource_type.replace("_", " ")}</Badge>
        <Badge variant={item.confidence < 0.75 ? "destructive" : "secondary"}>{Math.round(item.confidence * 100)}% sure</Badge>
        {item.origin === "generated" && <Badge variant="warning">generated</Badge>}
        {keptCount > 0 && <Badge variant="info">{keptCount} {keptCount === 1 ? "image" : "images"}</Badge>}
        {item.review_reasons.map((r) => <Badge key={r} variant="warning">{r.replace(/_/g, " ")}</Badge>)}
      </div>
      {state && <p className="text-sm"><Badge variant={state.variant}>{state.label}</Badge> {state.reason}</p>}
      {item.review_note && <p className="text-sm">Note: {item.review_note}</p>}
      {open && (
        <div className="space-y-3 border-t border-border pt-3">
          {!detail ? <Skeleton className="h-24 w-full" /> : item.kind === "module_topic"
            ? p.blocks.map((b: any, i: number) => <Block key={i} b={b} data={data} onRemoveFigure={canEditImages ? (id) => void changeFigure(id, "remove") : undefined} />)
            : <Question p={p} data={data} onRemoveFigure={canEditImages ? (id) => void changeFigure(id, "remove") : undefined} />}
          {discarded.length > 0 && (
            <details className="text-sm" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open && discarded.some((d) => !data[d.figure_external_id])) void loadDiscarded(); }}>
              <summary className="min-h-11 cursor-pointer py-2 font-semibold">Discarded images ({discarded.length})</summary>
              <ul className="space-y-2">
                {discarded.map((d) => (
                  <li key={d.figure_external_id} className="space-y-1 rounded-tile bg-soft p-3">
                    {data[d.figure_external_id] && <img src={data[d.figure_external_id]} alt={d.caption || "Discarded image"} className="max-h-40 rounded-lg" />}
                    <p className="text-sm">{d.reason}</p>
                    <p className="text-xs text-muted-foreground">Page {d.page}{d.caption ? ` · ${d.caption}` : ""}</p>
                    {canEditImages && <Button size="sm" variant="outline" loading={busy} onClick={() => void changeFigure(d.figure_external_id, "restore")}>Restore image</Button>}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {editing && (
            <div className="space-y-2">
              <textarea className="min-h-48 w-full rounded-lg border border-border bg-card p-2 font-mono text-sm" value={editText} onChange={(e) => setEditText(e.target.value)} aria-label="Edit item as JSON" />
              <Button size="sm" loading={busy} onClick={() => { try { void act(item.status, undefined, JSON.parse(editText)); } catch { showToast("error", "That is not valid JSON"); } }}>Save changes</Button>
            </div>
          )}
          {rejecting && (
            <div className="space-y-2">
              <textarea className="min-h-20 w-full rounded-lg border border-border bg-card p-2 text-base" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What is wrong? The next run will read this." aria-label="Rejection note" />
              <Button size="sm" variant="destructive" loading={busy} onClick={() => void act("rejected", note)}>Reject with note</Button>
            </div>
          )}
          {detail && detail.review_history.length > 0 && (
            <details className="text-xs text-muted-foreground"><summary>History</summary>{detail.review_history.map((h, i) => <p key={i}>{h.at.slice(0, 16)} · {h.status} · {h.by}{h.note ? ` · ${h.note}` : ""}</p>)}</details>
          )}
        </div>
      )}
      <div className="flex items-center justify-end gap-1 border-t border-border pt-3">
        <button type="button" onClick={() => setOpen(!open)} className="mr-auto min-h-11 px-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
          {open ? "Hide details" : "View details"}
        </button>
        {item.status !== "published" ? (
          <Button size="sm" loading={busy} onClick={() => (mode === "replace" ? askReplace(() => void act("published")) : void act("published"))}>Approve</Button>
        ) : (
          <Button size="sm" variant="outline" loading={busy} onClick={() => void act("draft")}>Back to draft</Button>
        )}
        {
          <>
            <button
              ref={menuRef}
              type="button"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-11 w-11 items-center justify-center rounded-control text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:ring-2 ring-ring"
            >
              <MoreHorizontal size={18} />
            </button>
            <ActionMenu open={menuOpen} onClose={() => setMenuOpen(false)} anchorRef={menuRef}>
              {item.status !== "published" && (
                <ActionMenuItem onClick={() => { setMenuOpen(false); setOpen(true); setEditing(!editing); setEditText(JSON.stringify(editable, null, 2)); }}>Edit content</ActionMenuItem>
              )}
              {item.status !== "rejected" && (
                <ActionMenuItem variant="destructive" onClick={() => { setMenuOpen(false); setOpen(true); setRejecting(true); }}>Reject with note</ActionMenuItem>
              )}
            </ActionMenu>
          </>
        }
      </div>
    </Card>
  );
}

export function AdminIngestReviewPage() {
  const token = useAdminToken();
  const { showToast } = useToast();
  const [params, setParams] = useSearchParams();
  const rawStatus = params.get("status");
  const status: IngestStatus = rawStatus === "published" || rawStatus === "rejected" ? rawStatus : "draft";
  const type = params.get("type") ?? "";
  const courseId = params.get("course") ?? "";
  const onlyReview = params.get("needs_review") === "1";
  const page = Math.max(1, Number(params.get("page")) || 1);
  /** Changing a filter resets to page 1; empty values are dropped from the URL. */
  const setFilter = (changes: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries({ page: "", ...changes })) { if (v) next.set(k, v); else next.delete(k); }
    setParams(next, { replace: true });
  };
  const setPage = (p: number) => setFilter({ page: p > 1 ? String(p) : "" });
  const [courses, setCourses] = useState<{ id: string; name: string; code: string }[]>([]);
  useEffect(() => { api.courses().then(setCourses).catch(() => setCourses([])); }, []);
  const [data, setData] = useState<{ items: IngestItem[]; total: number; page_size: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [chapterCounts, setChapterCounts] = useState<ChapterGroupCounts[]>([]);

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      setData(await api.admin.ingestReview.list(token, { status, resource_type: type || undefined, course_id: courseId || undefined, needs_review: onlyReview ? true : undefined, sort: type === "note" ? "chapter" : undefined, page }));
      if (type === "note") setChapterCounts((await api.admin.ingestReview.chapterCounts(token, { course_id: courseId || undefined, status: status || undefined })).chapters);
      setSel(new Set());
    } catch (e) { setError(mapApiError(e).message); }
  }, [token, status, type, courseId, onlyReview, page]);
  useEffect(() => { void load(); }, [load]);

  const [blocks, setBlocks] = useState<BridgeBlock[] | null>(null);
  const [deciding, setDeciding] = useState(false);
  const decide = async (choice: BlockChoice, ids: string[]) => {
    if (!token) return;
    setDeciding(true);
    try {
      if (choice === "auto_fix") {
        const chapters = new Set((blocks ?? []).filter((b) => b.kind === "invalid_notes" && b.chapter_id).map((b) => b.chapter_id as string));
        for (const chapter_id of chapters) await api.admin.ingestReview.autoFix(token, { chapter_id, dry_run: false, confirm_apply: true });
      }
      const opts = choice === "allow_duplicate" ? { allow_duplicate: true } : choice === "auto_fix" ? { on_conflict: mode === "replace" ? ("append" as const) : mode } : { on_conflict: choice };
      const r = await api.admin.ingestReview.bulk(token, ids, "published", undefined, opts);
      r.warnings.forEach((w) => showToast("info", w));
      setBlocks(r.blocked.length ? r.blocked : null);
      showToast(r.blocked.length ? "info" : "success", r.blocked.length ? "Some items still need a decision" : `${r.changed} approved`);
      await load();
    } catch (e) { showToast("error", mapApiError(e).message); } finally { setDeciding(false); }
  };

  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [bulkNote, setBulkNote] = useState("");
  const bulk = async (to: IngestStatus) => {
    if (!token || sel.size === 0) return;
    let note: string | undefined;
    if (to === "rejected") {
      note = bulkNote.trim();
      if (!note) { showToast("error", "Add a short note so the next run can use it"); return; }
    }
    setBusy(true);
    try {
      const r = await api.admin.ingestReview.bulk(token, [...sel], to, note, to === "published" ? modeOpts : {});
      setBulkNote(""); setBulkRejecting(false);
      r.warnings.forEach((w) => showToast("info", w));
      if (r.blocked.length) setBlocks(r.blocked);
      showToast(r.blocked.length ? "info" : "success", r.blocked.length ? `${r.changed} updated, some need a decision` : `${r.changed} updated`);
      await load();
    } catch (e) { showToast("error", mapApiError(e).message); } finally { setBusy(false); }
  };

  const [confirmAll, setConfirmAll] = useState(false);
  const [mode, setMode] = useState<ChapterMode>("append");
  const [replaceRun, setReplaceRun] = useState<(() => void) | null>(null);
  const modeOpts = { on_conflict: mode, confirm_replace: mode === "replace" } as const;
  const askReplace = (run: () => void) => setReplaceRun(() => run);
  const approveAll = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const r = await api.admin.ingestReview.approveAll(token, { status, resource_type: type || undefined, course_id: courseId || undefined, needs_review: onlyReview ? true : undefined, ...modeOpts });
      r.warnings.forEach((w) => showToast("info", w));
      showToast("success", `${r.approved} approved, ${r.skipped} skipped`);
      setConfirmAll(false);
      await load();
    } catch (e) { showToast("error", mapApiError(e).message); } finally { setBusy(false); }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const typeTabs = [["", "All"], ["note", "Notes"], ["quiz", "Quizzes"], ["past_exam", "Past exams"], ["coc_exam", "COC exams"]] as const;
  const selectCls = "min-h-11 rounded-control border border-border bg-card px-3 text-base sm:text-sm";
  return (
    <div className="space-y-6">
      <h1 className="font-display text-xl font-bold sm:text-2xl">Content review</h1>
      <div className="flex flex-wrap items-center gap-3">
        <select className={selectCls} value={status} onChange={(e) => setFilter({ status: e.target.value === "draft" ? "" : e.target.value })} aria-label="Status">
          <option value="draft">Draft</option><option value="published">Approved</option><option value="rejected">Rejected</option>
        </select>
        <select className={selectCls} value={courseId} onChange={(e) => setFilter({ course: e.target.value })} aria-label="Course">
          <option value="">All courses</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.code ? `${c.code} · ${c.name}` : c.name}</option>)}
        </select>
        <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={onlyReview} onChange={(e) => setFilter({ needs_review: e.target.checked ? "1" : "" })} className="h-5 w-5" />Needs review only</label>
        <span className="text-sm text-muted-foreground">{data ? `${data.total} items` : ""}</span>
        {status === "draft" && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">If a chapter already has content</span>
            <select className={selectCls} value={mode} onChange={(e) => setMode(e.target.value as ChapterMode)} aria-label="If a chapter already has content">
              <option value="append">Append to chapter</option><option value="replace">Replace chapter content</option><option value="new_chapter">Create new chapter</option>
            </select>
          </label>
        )}
        {status === "draft" && <Button size="sm" className="ml-auto" disabled={!data || data.total === 0} onClick={() => (mode === "replace" ? askReplace(() => setConfirmAll(true)) : setConfirmAll(true))}>Approve all</Button>}
      </div>
      <div role="tablist" aria-label="Type" className="flex flex-wrap gap-2">
        {typeTabs.map(([v, label]) => (
          <Button key={v || "all"} role="tab" aria-selected={type === v} size="sm" variant={type === v ? "primary" : "outline"} onClick={() => setFilter({ type: v })}>{label}</Button>
        ))}
      </div>
      <ConfirmDialog
        isOpen={!!replaceRun}
        title="Replace the chapter content?"
        message="The chapter's draft will be rebuilt from the approved items only. Students keep the live version until you publish, and older versions stay in the history."
        confirmText="Replace"
        variant="destructive"
        onCancel={() => setReplaceRun(null)}
        onConfirm={() => { const run = replaceRun; setReplaceRun(null); run?.(); }}
      />
      <ConfirmDialog
        isOpen={confirmAll}
        title="Approve all"
        message={`${data?.total ?? 0} items match the current filters. Items that need a decision or may be duplicates are skipped.`}
        confirmText="Approve all"
        loading={busy}
        onConfirm={() => void approveAll()}
        onCancel={() => setConfirmAll(false)}
      />
      <BulkBar
        count={sel.size}
        noun="items"
        busy={busy}
        total={data?.items.length}
        onSelectAll={() => setSel(new Set((data?.items ?? []).map((i) => i.id)))}
        onClear={() => setSel(new Set())}
        actions={[
          { key: "approve", label: "Approve", onClick: () => (mode === "replace" ? askReplace(() => void bulk("published")) : void bulk("published")) },
          { key: "reject", label: "Reject with note", danger: true, onClick: () => setBulkRejecting(!bulkRejecting) },
        ]}
      />
      {sel.size > 0 && bulkRejecting && (
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={bulkNote}
            onChange={(e) => setBulkNote(e.target.value)}
            placeholder="What is wrong? The next run will read this."
            aria-label="Rejection note for selected items"
            className="min-h-11 flex-1 rounded-control border border-border bg-card px-3 text-base sm:text-sm"
          />
          <Button size="sm" variant="destructive" loading={busy} onClick={() => void bulk("rejected")}>Reject selected</Button>
        </div>
      )}
      {data && data.items.length > 0 && (
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={data.items.every((i) => sel.has(i.id))}
            onChange={(e) => setSel(e.target.checked ? new Set(data.items.map((i) => i.id)) : new Set())}
          />
          Select all on this page
        </label>
      )}
      {error && <StatusMessage variant="error" title="Could not load items" message={error} />}
      {!data && !error && <Skeleton className="h-32 w-full" />}
      {data?.items.length === 0 && <p className="py-10 text-center text-base text-muted-foreground">Nothing here.</p>}
      {(() => {
        const card = (it: IngestItem) => (
          <ItemCard key={it.id} item={it} token={token!} selected={sel.has(it.id)} onChanged={() => void load()} onBlocked={setBlocks} mode={mode} askReplace={askReplace}
            onSelect={(v) => setSel((s) => { const n = new Set(s); if (v) n.add(it.id); else n.delete(it.id); return n; })} />
        );
        if (type !== "note") return <div className="space-y-4">{data?.items.map(card)}</div>;
        const groups = new Map<string, IngestItem[]>(); // the list is already sorted by chapter, then topic order
        for (const it of data?.items ?? []) {
          const k = `${it.course_id ?? ""}|${it.payload.chapter?.order ?? ""}`;
          groups.set(k, [...(groups.get(k) ?? []), it]);
        }
        return (
          <div className="space-y-6">
            {[...groups.entries()].map(([k, list]) => {
              const first = list[0];
              const order = Number(first.payload.chapter?.order) || 0;
              const counts = chapterCounts.find((c) => `${c.course_id ?? ""}|${c.order ?? ""}` === k);
              return (
                <IngestChapterGroup key={k} token={token!} courseId={first.course_id ?? ""} order={order} title={first.payload.chapter?.title ?? ""} counts={counts} shown={list.length}
                  mode={mode} canApprove={status === "draft" && !!first.course_id && order > 0} onDone={() => void load()}>
                  {list.map(card)}
                </IngestChapterGroup>
              );
            })}
          </div>
        );
      })()}
      <IngestBlockedDialog blocks={blocks} busy={deciding} onChoose={(c, ids) => void decide(c, ids)} onClose={() => setBlocks(null)} />
      {pages > 1 && (
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span className="text-sm">Page {page} of {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
